import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '@/i18n/index.jsx';
import { trackEvent, Events } from '@/utils/analytics.js';
import {
  getConsentStatus,
  getAnalyticsConsent,
  getAdsConsent,
  setConsent,
} from '@/utils/consent.js';

export default function CookieBanner() {
  const [isVisible, setIsVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [ads, setAds] = useState(false);
  const { lang } = useI18n();
  const isEN = lang === 'en';

  useEffect(() => {
    // Show the banner only when no explicit choice has been recorded.
    // Continued navigation is never treated as consent.
    if (getConsentStatus() === null) {
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  // Pre-fill toggles with any previously saved custom preferences.
  useEffect(() => {
    setAnalytics(getAnalyticsConsent() === 'granted');
    setAds(getAdsConsent() === 'granted');
  }, []);

  const handleAcceptAll = () => {
    setConsent({ status: 'accepted' });
    setIsVisible(false);
    trackEvent(Events.COOKIE_ACCEPT);
  };

  const handleRejectAll = () => {
    setConsent({ status: 'rejected' });
    setIsVisible(false);
    trackEvent(Events.COOKIE_REJECT);
  };

  const handleSavePreferences = () => {
    setConsent({
      status: 'custom',
      analytics: analytics ? 'granted' : 'denied',
      ads: ads ? 'granted' : 'denied',
    });
    setIsVisible(false);
    trackEvent('cookie_preferences');
  };

  if (!isVisible) return null;

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] max-w-[560px] bg-background border border-border shadow-lg rounded-xl z-50 overflow-hidden flex flex-col p-5"
      role="dialog"
      aria-modal="true"
      aria-label={isEN ? 'Cookies and Privacy Notice' : 'Aviso de Cookies e Privacidade'}
    >
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-foreground mb-1">
          {isEN ? 'Cookies & Privacy Notice' : 'Aviso de Cookies e Privacidade'}
        </h3>
        <p className="text-xs text-muted-foreground leading-relaxed">
          {isEN
            ? 'We use cookies to personalize content and ads and to improve your experience. You choose what to allow. By continuing to browse without choosing, no non-essential cookies are set. See our '
            : 'Utilizamos cookies para personalizar o conteúdo e os anúncios e melhorar a sua experiência. Você escolhe o que permitir. Ao continuar navegando sem escolher, nenhum cookie não essencial é definido. Veja nossa '}
          <Link to="/cookies-policy/" className="underline font-medium hover:text-foreground">
            {isEN ? 'Cookies Policy' : 'Política de Cookies'}
          </Link>.
        </p>
      </div>

      {showPrefs && (
        <div className="mb-4 space-y-3 border-t border-border/60 pt-4">
          <p className="text-xs font-semibold text-foreground">
            {isEN ? 'Manage preferences' : 'Gerenciar preferências'}
          </p>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={analytics}
              onChange={(e) => setAnalytics(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-border accent-[#111]"
            />
            <span>
              <span className="block text-xs font-medium text-foreground">
                {isEN ? 'Analytics' : 'Analíticos'}
              </span>
              <span className="block text-[11px] text-muted-foreground leading-snug">
                {isEN
                  ? 'Measure usage to improve the site (Google Analytics).'
                  : 'Medir o uso para melhorar o site (Google Analytics).'}
              </span>
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={ads}
              onChange={(e) => setAds(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-border accent-[#111]"
            />
            <span>
              <span className="block text-xs font-medium text-foreground">
                {isEN ? 'Advertising' : 'Publicidade'}
              </span>
              <span className="block text-[11px] text-muted-foreground leading-snug">
                {isEN
                  ? 'Personalized ads and ad measurement (Google AdSense).'
                  : 'Anúncios personalizados e medição de anúncios (Google AdSense).'}
              </span>
            </span>
          </label>
        </div>
      )}

      <div className="flex flex-col sm:flex-row items-center justify-end gap-3 mt-auto">
        <button
          onClick={() => setShowPrefs((v) => !v)}
          className="w-full sm:w-auto px-4 py-2 text-sm font-medium border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors rounded-lg"
        >
          {showPrefs
            ? isEN ? 'Back' : 'Voltar'
            : isEN ? 'Manage preferences' : 'Gerenciar preferências'}
        </button>
        <button
          onClick={handleRejectAll}
          className="w-full sm:w-auto px-4 py-2 text-sm font-medium border border-transparent text-muted-foreground hover:text-foreground hover:bg-muted transition-colors rounded-lg"
        >
          {isEN ? 'Reject all' : 'Recusar tudo'}
        </button>
        {showPrefs ? (
          <button
            onClick={handleSavePreferences}
            className="w-full sm:w-auto px-6 py-2 text-sm font-medium bg-[#111] text-white hover:bg-black transition-colors rounded-lg shadow-sm"
          >
            {isEN ? 'Save preferences' : 'Salvar preferências'}
          </button>
        ) : (
          <button
            onClick={handleAcceptAll}
            className="w-full sm:w-auto px-6 py-2 text-sm font-medium bg-[#111] text-white hover:bg-black transition-colors rounded-lg shadow-sm"
          >
            {isEN ? 'Accept all' : 'Aceitar tudo'}
          </button>
        )}
      </div>
    </div>
  );
}
